import React, { useEffect, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../shared";

/**
 * RoutineReveal — "His Day, Her Day" timeline for the present-tense lesson.
 *
 * One card per person (Omar / Maryam). Tapping the next-step button reveals
 * the next line of their day — verb highlighted so the يَـ / تَـ prefix
 * pattern is visible side by side — and plays it. Revealed steps stay as
 * plain rows with their own replay button (never nested in a disabled
 * button). Completes when both days are fully revealed.
 */
function HighlightedStep({ step }) {
  const at = step.verb ? step.ar.indexOf(step.verb) : -1;
  if (at === -1) return step.ar;
  return (
    <>
      {step.ar.slice(0, at)}
      <span style={{ color: "var(--gold-bright)" }}>{step.verb}</span>
      {step.ar.slice(at + step.verb.length)}
    </>
  );
}

export default function RoutineReveal({ data, onComplete, audio }) {
  const people = data.people;
  const [revealed, setRevealed] = useState(() => Object.fromEntries(people.map((p) => [p.id, 0])));

  const doneCount = people.filter((p) => revealed[p.id] >= p.steps.length).length;
  const done = doneCount >= people.length;

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const handleReveal = (person) => {
    const next = revealed[person.id] + 1;
    if (next > person.steps.length) return;
    setRevealed((prev) => ({ ...prev, [person.id]: next }));
    if (audio) audio.play(person.steps[next - 1].ar);
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-end mb-4">
        <ProgressPill>{doneCount}/{people.length} days told</ProgressPill>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {people.map((person) => {
          const shown = revealed[person.id];
          const atEnd = shown >= person.steps.length;
          return (
            <div
              key={person.id}
              className="rounded-xl px-4 py-4"
              style={{ background: "var(--ink)", border: "1px solid var(--panel-line)" }}
            >
              <div className="text-center mb-3">
                <p dir="rtl" lang="ar" className="font-arabic text-2xl leading-[1.6] m-0" style={{ color: "var(--gold-bright)" }}>
                  {person.name_ar}
                </p>
                <p className="text-xs m-0" style={{ color: "var(--parchment-dim)" }}>{person.name_en}</p>
              </div>

              <div className="flex flex-col gap-2.5">
                {person.steps.slice(0, shown).map((step) => (
                  <div
                    key={step.id}
                    className="rounded-lg px-3 py-2.5"
                    style={{ background: "var(--forest)", border: "1px solid var(--panel-line)" }}
                  >
                    <p className="m-0 flex items-center justify-between gap-2">
                      <span dir="rtl" lang="ar" className="font-arabic text-xl leading-[1.7] grow text-right" style={{ color: "var(--parchment)" }}>
                        <HighlightedStep step={step} />
                      </span>
                      {audio && (
                        <PlayButton text={step.ar} playingText={audio.playingText} onPlay={audio.play} />
                      )}
                    </p>
                    <p className="text-xs mt-1 mb-0" style={{ color: "var(--parchment-dim)" }}>{step.en}</p>
                    {step.note && (
                      <p className="text-xs mt-1.5 mb-0 leading-relaxed" style={{ color: "var(--gold-dim)" }}>
                        <MixedText text={step.note} />
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {!atEnd && (
                <div className="flex justify-center mt-3">
                  <button
                    type="button"
                    onClick={() => handleReveal(person)}
                    className="inline-flex items-center justify-center px-4 py-2 min-h-[40px] text-sm font-semibold rounded-lg transition-all hover:brightness-125 animate-pulse"
                    style={{ background: "var(--gold-bg)", color: "var(--gold-bright)", border: "1px dashed var(--gold-dim)" }}
                  >
                    {shown === 0 ? "Start the day" : "What next?"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {done && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">🌗</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.takeaway} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
